import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import GameCard from '@/components/feature/GameCard';
import FAQAccordion from '@/components/base/FAQAccordion';
import { games } from '@/data/games';

const featuredGames = [
  ...games.filter((g) => g.isPopular),
  ...games.filter((g) => !g.isPopular && g.isNew),
  ...games.filter((g) => !g.isPopular && !g.isNew),
].slice(0, 18);

const homepageFAQ = [
  {
    question: 'What are Yono Games?',
    answer: 'Yono Games is a collection of mobile gaming applications popular among Indian Android users. The Yono ecosystem includes rummy games, slot games, card games, bingo, spin games, and arcade-style entertainment. These apps are designed for Android devices with simple interfaces accessible to both new and experienced players across India.',
  },
  {
    question: 'How can I download Yono Games?',
    answer: 'You can download Yono Games and related apps directly from our website. Browse the game listings, click on any game card, and use the download button to get the latest APK file. All downloads are for Android devices. Before installing, enable installation from unknown sources in your device settings.',
  },
  {
    question: 'Are Yono Games available for Android?',
    answer: 'Yes, all Yono Games listed on our website are designed for Android devices running Android 5.0 or higher. Download the APK file, enable installation from unknown sources, and install the app on your device.',
  },
  {
    question: 'Do Yono Games offer bonuses?',
    answer: 'Yes, most Yono Games offer welcome bonuses for new players. Bonus amounts vary by game and are listed on each game card and detail page. Additional rewards may include daily login bonuses, referral rewards, and seasonal promotions.',
  },
  {
    question: 'What is the minimum withdrawal amount?',
    answer: 'Minimum withdrawal amounts vary by game, typically starting from ₹100. Each game card and detail page displays the specific minimum withdrawal for that app. Withdrawals are generally processed through UPI, bank transfer, and Indian e-wallets.',
  },
];

export default function Home() {
  const scrollToGames = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById('all-games')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title="All Yono Games 2026 – Latest Yono Apps and APK Downloads"
        description="Explore all Yono Games, new Yono apps, rummy games, slot games, bonuses and Android APK download pages for Indian users."
        keywords="Yono Games, All Yono Games, Yono Apps, Yono Games Download, New Yono Apps, Android Games India, Rummy Games"
      />

      <Header />

      <main>
        {/* Banner */}
        <section className="relative h-[170px] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-600"></div>
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10 h-full flex flex-col justify-center px-4">
            <span className="text-primary-100 text-[11px] font-semibold uppercase tracking-wide mb-1">
              2026 Updated Games
            </span>
            <h1 className="text-[22px] font-heading font-bold text-white leading-tight mb-1">
              All Yono Games – Latest Yono Apps and APK Downloads
            </h1>
            <p className="text-white/80 text-[13px] leading-snug mb-3 max-w-[320px]">
              Your trusted directory for Yono apps, rummy, slots, and Android game downloads in India.
            </p>
            <a
              href="#all-games"
              onClick={scrollToGames}
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-white text-foreground-900 rounded-full text-[13px] font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer self-start"
            >
              Browse Games
              <i className="ri-arrow-down-line text-sm"></i>
            </a>
          </div>
        </section>

        {/* Introduction */}
        <section className="px-4 pt-6 pb-3">
          <p className="text-foreground-600 text-[14px] leading-relaxed">
            Welcome to <strong>All Yono Games</strong> — your go-to directory for Yono apps and mobile gaming in India. We curate the best <Link to="/games" className="text-primary-600 font-medium hover:underline">rummy, slots, and casino-style apps</Link> available for Android, with honest details on bonuses, withdrawals, and APK downloads. Whether you're looking for classic Indian rummy tables, <Link to="/new-games" className="text-primary-600 font-medium hover:underline">newly launched Yono apps</Link>, or 777-themed games with jackpot spins, our handpicked collection of 90 games has you covered. No clutter, no fake listings — just real apps with real bonus info so you can download with confidence.
          </p>
        </section>

        {/* Featured Yono Games */}
        <section id="all-games" className="px-4 py-2 scroll-mt-16">
          <div className="mb-4">
            <h2 className="text-lg font-heading font-bold text-foreground-900">
              Featured Yono Games
            </h2>
            <p className="text-foreground-500 text-[12px] mt-0.5">
              Handpicked Yono apps with the best bonuses and fastest withdrawals.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {featuredGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>

          {/* View More Link */}
          <div className="mt-4 text-center">
            <Link
              to="/games"
              className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-background-100 text-foreground-800 rounded-full text-[13px] font-semibold hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              View All 90 Yono Games
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>

          {/* Bonus disclosure */}
          <p className="text-foreground-400 text-[10px] leading-relaxed mt-4 text-center">
            Bonus amounts, deposit requirements and withdrawal terms may change. Review the current offer terms before registering or depositing. See our <Link to="/disclaimer" className="text-primary-600 hover:underline">disclaimer</Link> for details.
          </p>
        </section>

        {/* SEO Info Section */}
        <section className="px-4 py-5 border-t border-background-200">
          <div className="space-y-4 text-foreground-600 text-[13px] leading-relaxed">
            <div>
              <h3 className="font-heading font-semibold text-foreground-900 text-[15px] mb-1.5">
                What Are Yono Games?
              </h3>
              <p>
                Yono Games is the umbrella term for a growing family of mobile gaming apps built for Indian Android users. The category spans rummy, slots, bingo, spin games, arcade titles, and casual entertainment — with new apps dropping every month. What makes these apps so popular is how well they run on budget and mid-range phones. Clean interfaces, lightweight APK installs, and seamless UPI payment support have turned Yono into a household name among Indian mobile gamers.
              </p>
              <p className="mt-2">
                Browse our <Link to="/games" className="text-primary-600 font-medium hover:underline">complete game directory</Link> to explore every Yono app we track, or jump straight to the <Link to="/new-games" className="text-primary-600 font-medium hover:underline">new releases page</Link> to see what just launched. Every listing includes the category, welcome bonus amount, and minimum withdrawal so you can compare at a glance.
              </p>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-foreground-900 text-[15px] mb-1.5">
                How to Download and Install Yono Apps
              </h3>
              <p>
                Each game on our site comes with its own APK download button. Tap it, save the file to your Android phone, and open it to install. Before your first install, you'll need to toggle on "Install from unknown sources" in device settings — takes two seconds and you only do it once. Every <Link to="/games" className="text-primary-600 font-medium hover:underline">game detail page</Link> walks you through the exact steps if anything is unclear.
              </p>
              <p className="mt-2">
                A quick word of caution: only download APKs from trusted sources like our directory, check app permissions before installing, and confirm that online gaming is legal in your state. We are an independent directory — not affiliated with any game developer. Read our full policy on the <Link to="/disclaimer" className="text-primary-600 font-medium hover:underline">disclaimer</Link> and <Link to="/terms" className="text-primary-600 font-medium hover:underline">terms of use</Link> pages.
              </p>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-foreground-900 text-[15px] mb-1.5">
                Bonuses, Deposits, and Withdrawals
              </h3>
              <p>
                New players can claim welcome bonuses ranging from ₹31 up to ₹599 depending on the app. Common offers include signup cash, deposit match bonuses, free spins, and referral rewards. Many platforms run daily login bonuses and seasonal promos during Diwali, Holi, and other festivals. When it's time to cash out, UPI, bank transfer, and major Indian e-wallets are supported — most with a ₹100 minimum and processing within 24 hours.
              </p>
              <p className="mt-2">
                Bonus terms change frequently and vary between apps. Always check the latest offer and wagering requirements on the app itself before depositing. We list bonuses as a reference — visit the <Link to="/disclaimer" className="text-primary-600 font-medium hover:underline">bonus disclosure page</Link> for more on how we present this information.
              </p>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-foreground-900 text-[15px] mb-1.5">
                Play Responsibly
              </h3>
              <p>
                Gaming is entertainment — not a side hustle or a way to make money. Set time and spending limits for yourself, never chase losses, and only play with funds you're comfortable losing. If your gaming habits ever feel hard to control, our <Link to="/responsible-gaming" className="text-primary-600 font-medium hover:underline">Responsible Gaming</Link> page has self-assessment tools, practical tips, and links to professional support resources. Never borrow money to gamble, and remember: this site is strictly for users 18 and above.
              </p>
              <p className="mt-2">
                Learn more about who we are on the <Link to="/about" className="text-primary-600 font-medium hover:underline">About Us</Link> page, or review our <Link to="/privacy-policy" className="text-primary-600 font-medium hover:underline">Privacy Policy</Link> to understand how we handle your data.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 py-5 border-t border-background-200">
          <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
            Frequently Asked Questions About Yono Games
          </h2>
          <FAQAccordion items={homepageFAQ} schema={true} />
          <p className="text-foreground-400 text-[11px] mt-4 text-center">
            Can't find what you're looking for? Browse our <Link to="/games" className="text-primary-600 hover:underline">full game library</Link> or check the <Link to="/new-games" className="text-primary-600 hover:underline">new arrivals</Link>.
          </p>
        </section>
      </main>

      <Footer />
    </>
  );
}